<template>
  <header class="lk-header">
    <div class="lk-header__topbar-wrapper">
      <div class="lk-header__topbar lk-header-container">
        <p class="lk-header__topbar-info">
          <span>Проконсультируем по вопросам работы с Axoft Online</span>
          <a href="tel:88007002855" class="lk-header__topbar-phone">8 800 700-28-55</a>
        </p>

        <ul class="lk-header__topbar-links">
          <li class="lk-header__topbar-links-item">
            <a class="lk-header__topbar-link" :href="globalLink" target="_blank" rel="noopener noreferrer">
              <GlobeSvg />
              <span>Перейти на AxoftGlobal</span>
            </a>
          </li>

          <li class="lk-header__topbar-links-item">
            <a class="lk-header__topbar-link" href="https://online.axoftglobal.ru/help-content">
              <QuestionSvg />
              <span>Справка</span>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div class="lk-header__main lk-header-container">
      <div class="lk-header__logo">
        <a href="https://online.axoftglobal.ru/order/list?pages=1" aria-label="Axoft Online">
          <AxoftOnlineSvg class="lk-header__logo-icon" />
        </a>
      </div>

      <nav class="lk-header__nav" aria-label="Основная навигация">
        <ul class="lk-header__menu">
          <li
            v-for="(item, index) in menu"
            :key="item.name"
            class="lk-header__menu-item"
            :class="{
              'lk-header__menu-item--dropdown': hasSubmenu(item),
              'lk-header__menu-item--active': isMenuItemActive(item),
              'lk-header__menu-item--open': openedMenuIndex === index,
            }"
            @mouseenter="openMenu(index)"
            @mouseleave="closeMenu"
          >
            <template v-if="hasSubmenu(item)">
              <button
                type="button"
                class="lk-header__menu-button"
                :aria-label="item.name"
                :aria-expanded="openedMenuIndex === index ? 'true' : 'false'"
                aria-haspopup="true"
              >
                <span>{{ item.name }}</span>
                <ArrowSmallDown class="lk-header__menu-icon" />
              </button>

              <transition name="submenu">
                <div v-if="openedMenuIndex === index" class="lk-header__submenu-wrapper">
                  <ul class="lk-header__submenu" role="menu">
                    <li
                      v-for="submenuItem in item.submenu"
                      :key="submenuItem.name"
                      class="lk-header__submenu-item"
                      :class="{
                        'lk-header__submenu-item--active': isRouteActive(submenuItem.link),
                      }"
                      @click="closeMenu"
                    >
                      <app-link class="lk-header__submenu-link" :to="submenuItem.link" open-external-in-current-tab>
                        {{ submenuItem.name }}
                      </app-link>
                    </li>
                  </ul>
                </div>
              </transition>
            </template>

            <template v-else>
              <app-link class="lk-header__menu-link" :to="item.link" open-external-in-current-tab>
                {{ item.name }}
              </app-link>
            </template>
          </li>
        </ul>

        <div class="lk-header__actions">
          <LkNotifications class="lk-header__notifications" />
          <div
            class="lk-header__user"
            @mouseenter="onDesktopOpenUserMenu"
            @mouseleave="onDesktopCloseUserMenu"
            v-click-outside="closeUserMenu"
          >
            <button
              type="button"
              class="lk-header__user-button"
              :title="userFullName"
              :aria-label="userFullName"
              :aria-expanded="isUserMenuOpen"
              aria-haspopup="true"
              @click="toggleUserMenu"
            >
              <UserSvg class="lk-header__user-button-icon" />
            </button>

            <transition name="submenu">
              <div v-if="isUserMenuOpen" class="lk-header__submenu-wrapper lk-header__submenu-wrapper--user">
                <ul class="lk-header__submenu" role="menu">
                  <li class="lk-header__submenu-item">
                    <router-link class="lk-header__submenu-link" :to="{ name: 'lk-security' }">
                      {{ $root.staticText.lk.settings }}
                    </router-link>
                  </li>

                  <li class="lk-header__submenu-item">
                    <a class="lk-header__submenu-link" :href="linkLogout">
                      {{ $root.staticText.lk.logout }}
                    </a>
                  </li>
                </ul>
              </div>
            </transition>
          </div>
        </div>

        <div class="lk-header__burger-wrapper">
          <LkBurgerBtn v-model="isMobileMenuOpen" class="lk-header__burger" />
        </div>

        <LkBurgerMenu v-model="isMobileMenuOpen" :menu="menu" />
      </nav>
    </div>
  </header>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import AxoftOnlineSvg from '@assets/lk/img/AxoftOnline.svg?inline'
import GlobeSvg from '@assets/lk/icons/globe.svg?inline'
import UserSvg from '@assets/lk/icons/user.svg?inline'
import QuestionSvg from '@assets/lk/icons/question-circle.svg?inline'
import ArrowSmallDown from '@assets/lk/icons/arrow-small-down.svg?inline'
import LkNotifications from './components/LkNotifications.vue'
import LkBurgerBtn from './components/LkBurgerBtn.vue'
import LkBurgerMenu from './components/LkBurgerMenu.vue'
import Colorable from '@assets/js/vuetify'

export default {
  name: 'LkHeader',
  components: {
    Colorable,
    AxoftOnlineSvg,
    GlobeSvg,
    UserSvg,
    QuestionSvg,
    ArrowSmallDown,
    LkNotifications,
    LkBurgerBtn,
    LkBurgerMenu,
  },
  data() {
    return {
      openedMenuIndex: null,
      isUserMenuOpen: false,
      isMobileMenuOpen: false,
    }
  },
  computed: {
    ...mapState('mobile', ['isLkMobile']),
    ...mapGetters('lk', ['profile', 'linkLogout']),

    globalLink() {
      return __AXOFT__?.axGlobalUrl
    },
    menu() {
      return this.$root?.lk?.menu ?? []
    },
    userFullName() {
      const name = this.profile?.name ?? ''
      const surname = this.profile?.surname ?? ''
      return `${name} ${surname}`.trim()
    },
  },
  methods: {
    hasSubmenu(item) {
      return Array.isArray(item?.submenu) && item.submenu.length > 0
    },
    openMenu(index) {
      this.openedMenuIndex = index
    },
    closeMenu() {
      this.openedMenuIndex = null
    },
    isRouteActive(routeName) {
      return Boolean(routeName) && this.$route.name === routeName
    },
    isMenuItemActive(item) {
      if (this.hasSubmenu(item)) {
        return item.submenu.some(submenuItem => this.isRouteActive(submenuItem?.link))
      }

      return this.isRouteActive(item?.link)
    },
    onDesktopOpenUserMenu() {
      if (!this.isLkMobile) {
        this.isUserMenuOpen = true
      }
    },
    onDesktopCloseUserMenu() {
      if (!this.isLkMobile) {
        this.isUserMenuOpen = false
      }
    },
    closeUserMenu() {
      this.isUserMenuOpen = false
    },
    toggleUserMenu() {
      if (this.isLkMobile) {
        this.isUserMenuOpen = !this.isUserMenuOpen
      }
    },
  },
  watch: {
    $route() {
      this.isMobileMenuOpen = false
      this.isUserMenuOpen = false
      this.closeMenu()
    },

    isUserMenuOpen(v) {
      console.log('isUserMenuOpen', v)
    },
  },
}
</script>

<style lang="sass">
@import "~@src/assets/lk/styles/variables"

.lk-header
  width: 100%
  color: #fff
  background-color: $dark-blue
  font-family: 'Segoe UI', sans-serif

  @media (max-width: $header-screen-md)
    box-shadow: none

  &__topbar-wrapper
    border-bottom: 1px solid #E2E6F1
    font-size: 14px

    @media (max-width: $header-screen-md)
      display: none

  &__topbar
    height: var(--header-top-height)
    display: flex
    align-items: center
    justify-content: space-between

  &__topbar-info
    display: flex
    align-items: center
    gap: 10px

  &__topbar-phone
    color: #fff
    font-weight: 600
    transition: color .3s

    &:hover
      color: $bright-blue

  &__topbar-links
    display: flex
    gap: 32px
    font-weight: 600

  &__topbar-links-item
    display: flex

  &__topbar-link
    display: flex
    align-items: center
    gap: 8px
    transition: color .3s

    &:hover
      color: $bright-blue

  &__main
    display: flex
    align-items: center
    justify-content: space-between
    height: var(--header-bottom-height)

    @media (max-width: $header-screen-md)
      gap: 20px
      padding-inline: 32px 24px

  &__burger-wrapper
    display: none

    @media (max-width: $header-screen-md)
      display: flex
      gap: 23px
      align-items: center

      &:before
        content: ''
        display: inline-block
        width: 1px
        height: 21.5px
        background-color: #484f67
        border-radius: 1px

  &__logo
    flex: 1

    &-icon
      max-width: 208px
      display: inline-block
      vertical-align: middle
      height: 100%
      width: 100%

      @media (max-width: $header-screen-md)
        max-width: 180px

  &__nav
    display: flex
    align-items: center

    @media (max-width: $header-screen-md)
      gap: 20px

  &__menu
    display: flex
    align-items: center
    font-weight: 600
    margin-left: 11px

    @media (max-width: $header-screen-md)
      display: none

  &__menu-item
    display: flex
    cursor: pointer
    margin: 0 12px
    line-height: var(--header-bottom-height)

    @media (max-width: $header-screen-lg)
      margin: 0 6px

    &--open
      .lk-header__menu-button
        color: $bright-blue

      .lk-header__menu-icon
        margin-top: 4px
        transform: rotate(180deg)

    &--dropdown
      position: relative

    &--active
      .lk-header__menu-button,
      .lk-header__menu-link
        color: $bright-blue

  .router-link-active
    color: $bright-blue

  &__menu-button, &__menu-link
    font-size: 14px

    @media (max-width: $screen-tablet)
      font-size: 12px

  &__menu-button
    display: flex
    align-items: center
    gap: 6px
    background: transparent
    border: 0
    color: inherit

  &__menu-link
    @media (min-width: $desktop-medium)
      font-size: 14px

  &__menu-icon
    margin-top: 2px
    transition: transform .3s
    will-change: transform
    width: 20px
    height: 20px

  &__menu-button, &__menu-link, &__submenu-link
    transition: color .3s

    &:hover
      color: $bright-blue

  &__submenu-wrapper
    position: absolute
    top: 100%
    left: 0
    z-index: 1000
    padding-top: 6px

    &--user
      left: auto
      right: 0

  &__submenu
    display: flex
    flex-direction: column
    padding-block: 6px
    min-width: max-content
    border-radius: 8px
    background-color: #141C34
    font-size: 14px
    font-weight: 400
    line-height: normal
    opacity: .85

  &__submenu-item
    display: inline-flex

    &--active
      .lk-header__submenu-link
        color: $bright-blue

  &__submenu-link
    display: block
    width: 100%
    padding: 8px 16px

  &__actions
    display: flex
    align-items: center

    @media (max-width: $header-screen-md)
      gap: 20px

    > *
      position: relative

      &:before
        content: ''
        display: inline-block
        width: 1px
        height: 14px
        margin-inline: 24px
        border-radius: 1px
        background-color: #484F67

        @media (max-width: $header-screen-lg)
          margin-inline: 12px

    @media (max-width: $screen-tablet)
      > *:before
        margin-inline: 12px

    @media (max-width: $header-screen-md)
      > *:before
        display: none

  &__notifications
    display: flex
    align-items: center

  &__user
    display: flex
    align-items: center

  &__user-button
    position: relative
    width: 44px
    height: var(--header-bottom-height)
    border: none
    background: none
    color: #fff
    cursor: pointer
    transition: color .3s
    will-change: color

    &:hover
      color: $bright-blue

    @media (max-width: $header-screen-md)
      &:hover
        color: $white

    &-icon
      display: block
      margin-inline: auto
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/lk/header/LkHeader.vue